import React from 'react';
import { CardRoot, CardHeader, CardTitle, CardContent, CardFooter } from './primitives/card';
import cn from './primitives/cn';

/**
 * The white panel every CMS screen is built out of.
 *
 * The API has not changed: `title`, `subtitle`, `color`, `isDetail`, `padded`,
 * `className`, `bodyClassName`, `bodyStyle`, `columnClassName`, `footer`. 32 call sites
 * keep working untouched. Underneath it is now shadcn's Card.
 *
 * **What is visibly different, and it is the point:** `.module` was a bare white
 * rectangle with no edge at all, so against the grey page background adjacent panels ran
 * together and a card's bottom was wherever its content happened to stop. It now has the
 * hairline border and 5px radius the modals already use, and a rule under the header.
 *
 * **Why the legacy classes are still emitted.** Six rules in main.css reach into a
 * `.module` from outside it, and the first one matters a great deal:
 *
 *     .module a                       { text-decoration: underline; color: brand }
 *     .module .col-xs-12              { padding-left: 0 }
 *     .col-xs-12 .module-body .row    { margin-left: 0 }
 *     .module.fullscreen .module-body { position: absolute; ... }
 *     .module-body .content           { padding: 10px 0 }
 *     .modal-content .module          { border: none; margin: 0 20px }
 *
 * `.module a` styles every link inside every card. Dropping the class would strip the
 * underline and the brand colour from links on 32 screens at once, which is a content
 * change rather than a restyle. The classes stay as hooks, Tailwind does the painting,
 * and `important: true` in tailwind.config.js is what makes the paint win.
 *
 * `color` and `isDetail` still emit `.module.red` and friends. Nothing in the stylesheet
 * defines `.red` on a module today, so it is inert markup, but `moduleColor` is bound
 * from plugin config and a tenant stylesheet could be relying on it.
 */
const Card = ({
  title,
  subtitle = false,
  color = 'red',
  isDetail = false,
  padded = false,
  /**
   * This card holds a list screen.
   *
   * A list is not a card. Collections and the thirty-four other screens built on
   * `CmsItemListSection` put the table straight on the page: title top left, actions top
   * right in the header row, search under them, and the table running the full width. The
   * ten screens that reach for `Card` instead got a bordered panel inset inside the page
   * gutter, with their actions stranded inside it next to the search box, so the same
   * kind of screen came in two shapes depending on which component it was built from.
   *
   * `list-section` is the class main.css uses to strip the card and let the table out to
   * the full width; `ListToolbar` moves the actions up to the page header on its own.
   */
  list = false,
  className,
  bodyClassName,
  bodyStyle,
  columnClassName = 'col-sm-12',
  footer,
  children,
}) => {
  const body = (
    <CardContent
      className={cn(
        // A list screen's body supplies no inset of its own: the table runs the full
        // width and the toolbar sits on the page gutter, exactly as on the screens built
        // from `CmsItemListSection`. `cn` is tailwind-merge, so `tw-p-0` replaces the
        // primitive's `tw-px-4 tw-py-4` rather than fighting it.
        list && 'tw-p-0',
        'module-body',
        padded && !list && 'padded-bottom',
        bodyClassName
      )}
      style={bodyStyle}
    >
      {children}
    </CardContent>
  );

  return (
    <CardRoot
      className={cn(
        'module',
        'row',
        // The chrome has to come off here, not in main.css. The primitive's border,
        // radius and shadow are Tailwind utilities and `important: true` makes them
        // `!important`, so a stylesheet rule cannot take them away. tailwind-merge drops
        // the ones these replace.
        list && 'list-section tw-border-0 tw-rounded-none tw-shadow-none',
        className,
        !isDetail && color,
        'tw-mx-0 tw-mb-4'
      )}
    >
      {/* `subtitle` is this app's way of saying "no visible title": the class it adds is
          `display: none` in main.css, and ten screens pass a title with it purely so the
          card has a name in the source. The header used to be `padding: 0` with no rule,
          so an empty one collapsed and nothing showed. Now that CardHeader has its own
          padding and a bottom border, an empty one is a blank 28px band with a line under
          it at the top of the card. There is nothing to put in it, so it is not rendered. */}
      {title && !subtitle && (
        <CardHeader className="module-header">
          <CardTitle className="module-title">{title}</CardTitle>
        </CardHeader>
      )}
      {columnClassName ? <div className={columnClassName}>{body}</div> : body}
      {footer && <CardFooter className="row module-footer">{footer}</CardFooter>}
    </CardRoot>
  );
};

export default Card;
